import { Upload, Search, Bookmark, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import codePreview from "@/assets/code-preview.png";
export const WhatWeDoSection = () => {
  return <section className="pt-12 pb-0 bg-background overflow-hidden">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold">​How the Vault Works     </h2>
        </div>
        
        {/* Flow Container */}
        <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative">
          {/* Step 1: Upload */}
          <div className="relative group animate-fade-in">
            <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-xl shadow-lg z-10">
              1
            </div>
            
            <Card className="p-6 h-full border-2 hover:border-blue-500/50 transition-all hover:shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Upload className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold">Upload Your Macro</h3>
              </div>
              
              <p className="text-muted-foreground mb-4">
                Share your VBA scripts with the community. Add a title, description, and paste your code.
              </p>
              
              {/* Mini code preview */}
              <div className="bg-muted rounded-lg p-3 text-xs font-mono">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div className="text-blue-600 dark:text-blue-400">Sub</div>
                <div className="text-muted-foreground pl-4">Your VBA code here...</div>
                <div className="text-blue-600 dark:text-blue-400">End Sub</div>
              </div>
            </Card>

            {/* Arrow - Desktop only */}
            <div className="hidden md:block absolute top-1/2 -right-10 -translate-y-1/2">
              <ArrowRight className="w-8 h-8 text-muted-foreground" />
            </div>
          </div>

          {/* Step 2: Browse & Discover */}
          <div className="relative group animate-fade-in" style={{
          animationDelay: '0.2s'
        }}>
            <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold text-xl shadow-lg z-10">
              2
            </div>
            
            <Card className="p-6 h-full border-2 hover:border-purple-500/50 transition-all hover:shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <Search className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold">Browse the Vault</h3>
              </div>
              
              <p className="text-muted-foreground mb-4">
                Explore hundreds of VBA macros from analysts, developers, and Excel experts worldwide.
              </p>
              
              {/* Mock macro card preview */}
              <div className="bg-gradient-to-br from-blue-500/10 to-green-500/10 rounded-lg p-4 border">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center text-white font-bold text-xs">
                    M
                  </div>
                  <div className="flex-1">
                    <div className="h-2 bg-foreground/60 rounded w-3/4 mb-1"></div>
                    <div className="h-1.5 bg-muted-foreground/30 rounded w-1/2"></div>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="h-1 bg-muted-foreground/20 rounded"></div>
                  <div className="h-1 bg-muted-foreground/20 rounded w-4/5"></div>
                </div>
              </div>
            </Card>

            {/* Arrow - Desktop only */}
            <div className="hidden md:block absolute top-1/2 -right-10 -translate-y-1/2">
              <ArrowRight className="w-8 h-8 text-muted-foreground" />
            </div>
          </div>

          {/* Step 3: Save & Grow */}
          <div className="relative group animate-fade-in" style={{
          animationDelay: '0.4s'
        }}>
            <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-teal-500 text-white flex items-center justify-center font-bold text-xl shadow-lg z-10">
              3
            </div>
            
            <Card className="p-6 h-full border-2 hover:border-teal-500/50 transition-all hover:shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center">
                  <Bookmark className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                </div>
                <h3 className="text-xl font-semibold">Build Your Collection</h3>
              </div>
              
              <p className="text-muted-foreground mb-4">
                Save macros you love and build your personal library. Access them anytime from your dashboard.
              </p>
              
              {/* Saved macros visualization */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-muted rounded p-2">
                  <div className="w-6 h-6 rounded bg-blue-500/20 mb-1"></div>
                  <div className="h-1 bg-muted-foreground/30 rounded mb-1"></div>
                  <div className="h-1 bg-muted-foreground/20 rounded w-3/4"></div>
                </div>
                <div className="bg-muted rounded p-2">
                  <div className="w-6 h-6 rounded bg-purple-500/20 mb-1"></div>
                  <div className="h-1 bg-muted-foreground/30 rounded mb-1"></div>
                  <div className="h-1 bg-muted-foreground/20 rounded w-3/4"></div>
                </div>
                <div className="bg-muted rounded p-2">
                  <div className="w-6 h-6 rounded bg-teal-500/20 mb-1"></div>
                  <div className="h-1 bg-muted-foreground/30 rounded mb-1"></div>
                  <div className="h-1 bg-muted-foreground/20 rounded w-3/4"></div>
                </div>
                <div className="bg-muted rounded p-2 flex items-center justify-center">
                  <span className="text-2xl text-muted-foreground">+</span>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          
        </div>
      </div>
    </section>;
};